import React from 'react';
import { motion } from 'motion/react';
import { AnalysisResult, BusinessIdea, FeatureType } from '@/src/types';

interface Props {
  data: AnalysisResult | null;
  featureType: string;
  onSelectIdea: (idea: BusinessIdea) => void;
  isSpecificAnalysis?: boolean;
  onAnalyzeLocationOnly?: () => void;
  loading?: boolean;
}

// Launchpad Square Tactical Scanning Animation
const LaunchpadSquareScan = () => (
  <div className="flex flex-col items-center justify-center py-24 space-y-8">
    <div className="relative w-32 h-32 flex items-center justify-center">
      {/* Outer Square Bracket Borders */}
      <div className="absolute inset-0 border-2 border-[#F26522]/30 rounded-none" />
      
      {/* Precision corner brackets */}
      <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-[#F26522]" />
      <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-[#F26522]" />
      <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-[#F26522]" />
      <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-[#F26522]" />

      {/* Grid Pattern Inside Square */}
      <div className="absolute inset-2 grid grid-cols-3 grid-rows-3 gap-1 opacity-20 pointer-events-none">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="border border-[#F26522]" />
        ))}
      </div>

      {/* Concentric Pulsing Nested Squares */}
      <motion.div
        animate={{
          scale: [0.6, 1.15, 0.6],
          opacity: [0.3, 0.9, 0.3],
          rotate: [0, 90, 180],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="w-16 h-16 border-2 border-[#F26522]/70 rounded-none"
      />

      <motion.div
        animate={{
          scale: [0.85, 0.45, 0.85],
          opacity: [0.8, 0.2, 0.8],
          rotate: [180, 90, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute w-10 h-10 border border-[#F26522] bg-[#F26522]/10 rounded-none"
      />

      {/* Center Core Square */}
      <div className="absolute w-3 h-3 bg-[#F26522] shadow-[0_0_12px_#F26522] animate-ping" />
      <div className="absolute w-2.5 h-2.5 bg-[#FFFFFF]" />

      {/* Horizontal Scanning Laser Bar moving vertically across the square */}
      <motion.div
        animate={{
          top: ['4px', '120px', '4px'],
          opacity: [0.6, 1, 0.6],
        }}
        transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute left-1 right-1 h-0.5 bg-[#F26522] shadow-[0_0_10px_#F26522] pointer-events-none z-10"
      />
    </div>

    <div className="text-center space-y-2">
      <div className="flex items-center justify-center gap-2">
        <span className="w-1.5 h-1.5 bg-[#F26522] animate-pulse" />
        <span className="text-xs font-bold uppercase tracking-[0.35em] text-white font-heading">
          Assessing Market Opportunities
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-dm">
        Tactical Grid Reconnaissance In Progress
      </p>
    </div>
  </div>
);

// Discovery Hub Circular Radar Scanning Animation
const DiscoveryCircularRadar = () => (
  <div className="flex flex-col items-center justify-center py-24 space-y-8">
    <div className="relative w-36 h-36 flex items-center justify-center">
      {/* Outer Concentric Radar Circles */}
      <div className="absolute inset-0 rounded-full border border-emerald-500/20" />
      <div className="absolute inset-4 rounded-full border border-emerald-500/30" />
      <div className="absolute inset-8 rounded-full border border-emerald-500/40" />

      {/* Crosshairs */}
      <div className="absolute inset-x-0 top-1/2 h-px bg-emerald-500/20 -translate-y-1/2 pointer-events-none" />
      <div className="absolute inset-y-0 left-1/2 w-px bg-emerald-500/20 -translate-x-1/2 pointer-events-none" />

      {/* Expanding Ripple Waves */}
      <motion.div
        animate={{
          scale: [0.3, 1.3],
          opacity: [0.9, 0],
        }}
        transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: "easeOut",
        }}
        className="absolute inset-0 rounded-full border border-emerald-400"
      />
      <motion.div
        animate={{
          scale: [0.3, 1.3],
          opacity: [0.9, 0],
        }}
        transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: "easeOut",
          delay: 0.8,
        }}
        className="absolute inset-0 rounded-full border border-emerald-400"
      />

      {/* Rotating Radar Sweep Line with Gradient Trail */}
      <motion.div
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute inset-0 rounded-full pointer-events-none overflow-hidden"
      >
        <div 
          className="w-1/2 h-1/2 absolute top-0 right-0 origin-bottom-left"
          style={{
            background: 'conic-gradient(from 0deg at 0% 100%, rgba(16, 185, 129, 0.4) 0deg, rgba(16, 185, 129, 0.1) 45deg, transparent 90deg)',
          }}
        />
        {/* Sweep Edge Line */}
        <div className="absolute top-0 left-1/2 w-0.5 h-1/2 bg-emerald-400 shadow-[0_0_8px_#10B981] origin-bottom" />
      </motion.div>

      {/* Blinking Radar Target Ping */}
      <motion.div
        animate={{
          opacity: [0, 1, 0.2, 1, 0],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.6,
        }}
        className="absolute top-7 right-9 w-2 h-2 rounded-full bg-emerald-300 shadow-[0_0_8px_#6EE7B7]"
      />

      {/* Center Radar Origin */}
      <div className="relative z-10 w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_10px_#10B981]" />
      <div className="absolute z-10 w-1 h-1 rounded-full bg-white" />
    </div>

    <div className="text-center space-y-2">
      <div className="flex items-center justify-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
        <span className="text-xs font-bold uppercase tracking-[0.35em] text-white font-heading">
          Mapping District Intelligence
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-dm">
        Circular Density Scanner Active
      </p>
    </div>
  </div>
);

const LoadingLoop = ({ isDiscovery }: { isDiscovery?: boolean }) => {
  return isDiscovery ? <DiscoveryCircularRadar /> : <LaunchpadSquareScan />;
};

const AnalysisResultView: React.FC<Props> = ({ data, onSelectIdea, featureType, loading }) => {
  const isDiscoveryFlow = featureType === FeatureType.DensityScout || featureType === FeatureType.ReverseDiscovery;

  if (loading) {
    return (
      <div className="space-y-12 animate-fade-in pb-24 max-w-7xl mx-auto w-full px-4">
        <LoadingLoop isDiscovery={isDiscoveryFlow} />
      </div>
    );
  }

  if (!data) return null;

  const ideas = Array.isArray(data.ideas) ? data.ideas : [];
  const hasIdeas = ideas.length > 0;

  return (
    <div className="space-y-12 animate-fade-in pb-24 max-w-7xl mx-auto w-full px-4">
      {/* Executive Briefing Header */}
      <div className={`p-6 sm:p-10 ${isDiscoveryFlow ? 'bg-[#3a3430] border-emerald-500/30' : 'bg-[#3a3430] border-white/[0.06]'} border rounded-xl relative overflow-hidden`}>
        <div className={`absolute top-0 left-0 w-1.5 h-full ${isDiscoveryFlow ? 'bg-emerald-500' : 'bg-[#F26522]'}`}></div>
        <div className="space-y-4">
          <span className={`text-[10px] font-bold uppercase tracking-[0.3em] ${isDiscoveryFlow ? 'text-emerald-400' : 'text-[#F26522]'} font-heading`}>
            {isDiscoveryFlow ? 'District Reconnaissance' : 'Executive Summary'}
          </span>
          <div className="space-y-3">
            {(data.summary || 'Analysis complete.').split('\n').filter(Boolean).map((para, i) => (
              <p key={`summary-para-${i}`} className="text-xl sm:text-2xl font-medium italic leading-snug text-white">
                "{para.trim().replace(/^"/, '').replace(/"$/, '')}"
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* Intelligence Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between px-2">
          <h4 className="text-[10px] font-bold text-white/50 uppercase tracking-[0.3em] font-heading">
            {isDiscoveryFlow ? 'District Identities' : 'Market Opportunities'}
          </h4>
          <div className="flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full ${isDiscoveryFlow ? 'bg-emerald-500' : 'bg-[#F26522]'} animate-pulse`}></span>
            <span className="text-[10px] font-bold text-white/60 font-heading">LIVE INTEL</span>
          </div>
        </div>

        {!hasIdeas && (
          <div className="p-10 bg-[#3a3430] border border-white/[0.06] rounded-xl text-center">
            <p className="text-sm text-white/80 font-medium">No venture suggestions returned for this scan.</p>
            <p className="text-xs text-white/50 mt-2">Try adjusting your location or sector parameters.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {ideas.map((idea, idx) => {
            if (!idea || (!idea.id && !idea.title)) return null;
            return (
              <div 
                key={`idea-${idea.id || idea.title}-${idx}`} 
                className={`group bg-[#3a3430] border rounded-xl flex flex-col relative transition-all overflow-hidden ${isDiscoveryFlow ? 'border-emerald-500/20 hover:border-emerald-500/40' : 'border-white/[0.06] hover:border-white/[0.15]'}`}
              >
                <div 
                  onClick={() => onSelectIdea(idea)}
                  className="p-6 sm:p-10 space-y-6 sm:space-y-8 cursor-pointer hover:bg-white/[0.02] transition-all flex-1"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-2">
                     <div className="space-y-1">
                       <span className={`text-[9px] font-bold uppercase tracking-widest block font-heading ${isDiscoveryFlow ? 'text-emerald-400' : 'text-[#F26522]'}`}>
                         {isDiscoveryFlow ? 'Regional Domain' : idea.category}
                       </span>
                       <h3 className={`text-2xl sm:text-3xl font-medium text-white font-heading leading-tight transition-colors ${isDiscoveryFlow ? 'group-hover:text-emerald-400' : 'group-hover:text-[#F26522]'}`}>
                         {idea.title}
                       </h3>
                     </div>
                  </div>
                  <p className="text-sm sm:text-base text-white/60 font-normal leading-relaxed italic border-l-2 border-white/10 pl-4">
                    {idea.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AnalysisResultView;
